// ./src/theme/index.js
import { createTheme } from "@mantine/core"

export const theme = createTheme({
  colorScheme: "light",
  primaryColor: "brand",
  colors: {
    // FormSubmits brand - Deep Indigo/Violet
    // Conveys: Security, trust, sophistication, technical excellence
    brand: [
      "#f3f1ff", // Lightest - backgrounds, subtle highlights
      "#e4e0ff", // Very light - hover states
      "#cdc6ff", // Light - secondary backgrounds
      "#a99aff", // Medium light - borders, dividers
      "#8b78ff", // Medium - secondary buttons
      "#6c5ce7", // Primary brand color - main CTAs
      "#5a4bd1", // Darker - hover on primary
      "#4839a8", // Dark - active states
      "#372c82", // Very dark - text on light
      "#271f5c", // Darkest - headings
    ],
    // Accent - Electric Cyan
    // For highlights, badges, special elements
    accent: [
      "#e6fcff",
      "#c4f5fc",
      "#8eedf7",
      "#4de1f0",
      "#00d4e8", // Main accent
      "#00b8d4",
      "#009db8",
      "#007a91",
      "#005a6b",
      "#003d49",
    ],
    // Success - Validation passed, clean data
    // Emerald green - feels modern and trustworthy
    success: [
      "#ecfdf5",
      "#d1fae5",
      "#a7f3d0",
      "#6ee7b7",
      "#34d399",
      "#10b981", // Main success
      "#059669",
      "#047857",
      "#065f46",
      "#064e3b",
    ],
    // Error - Validation failed, blocked data
    // Coral red - urgent but not aggressive
    error: [
      "#fef2f2",
      "#fee2e2",
      "#fecaca",
      "#fca5a5",
      "#f87171",
      "#ef4444", // Main error
      "#dc2626",
      "#b91c1c",
      "#991b1b",
      "#7f1d1d",
    ],
    // Warning - Caution states
    warning: [
      "#fffbeb",
      "#fef3c7",
      "#fde68a",
      "#fcd34d",
      "#fbbf24",
      "#f59e0b", // Main warning
      "#d97706",
      "#b45309",
      "#92400e",
      "#78350f",
    ],
    // Neutral - Slate grays with slight blue undertone
    // Professional, technical feel
    neutral: [
      "#ffffff", // White
      "#f8fafc", // Off-white backgrounds
      "#f1f5f9", // Light gray backgrounds
      "#e2e8f0", // Borders, dividers
      "#cbd5e1", // Disabled states
      "#94a3b8", // Placeholder text
      "#64748b", // Secondary text
      "#475569", // Body text
      "#1e293b", // Headings
      "#0f172a", // Near black
    ],
  },
  // Space Grotesk for headings - geometric, technical, distinctive
  // IBM Plex Sans for body - clean, readable, professional
  fontFamily:
    "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  headings: {
    fontFamily: "'Space Grotesk', 'IBM Plex Sans', sans-serif",
    fontWeight: "700",
    sizes: {
      h1: { fontSize: "3.5rem", lineHeight: "1.1", fontWeight: "800" },
      h2: { fontSize: "2.25rem", lineHeight: "1.2", fontWeight: "700" },
      h3: { fontSize: "1.5rem", lineHeight: "1.3", fontWeight: "600" },
      h4: { fontSize: "1.25rem", lineHeight: "1.4", fontWeight: "600" },
      h5: { fontSize: "1rem", lineHeight: "1.5", fontWeight: "600" },
      h6: { fontSize: "0.875rem", lineHeight: "1.5", fontWeight: "600" },
    },
  },
  spacing: {
    xs: "0.5rem",
    sm: "0.75rem",
    md: "1rem",
    lg: "1.5rem",
    xl: "2rem",
    "2xl": "3rem",
    "3xl": "4rem",
  },
  radius: {
    xs: "0.25rem",
    sm: "0.375rem",
    md: "0.5rem",
    lg: "0.75rem",
    xl: "1rem",
    "2xl": "1.5rem",
  },
  shadows: {
    xs: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    sm: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
    md: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
    lg: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
    xl: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
  },
  other: {
    // Primary brand colors
    brandPrimary: "#6c5ce7",
    brandDark: "#271f5c",
    brandLight: "#f3f1ff",

    // Accent for highlights
    accentCyan: "#00d4e8",

    // Semantic colors
    successGreen: "#10b981",
    errorRed: "#ef4444",
    warningAmber: "#f59e0b",

    // Background variations
    bgPrimary: "#ffffff",
    bgSecondary: "#f8fafc",
    bgTertiary: "#f1f5f9",

    // Text colors
    textPrimary: "#1e293b",
    textSecondary: "#64748b",
    textDimmed: "#94a3b8",

    // Border colors
    borderLight: "#e2e8f0",
    borderMedium: "#cbd5e1",

    // Gradients for hero sections and CTAs
    gradientPrimary: "linear-gradient(135deg, #6c5ce7 0%, #8b78ff 100%)",
    gradientHero:
      "linear-gradient(135deg, #271f5c 0%, #6c5ce7 50%, #00d4e8 100%)",
    gradientSubtle: "linear-gradient(180deg, #f3f1ff 0%, #ffffff 100%)",
  },
})
